import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authFehler, authFehlerArt } from "../pages/konto/auth";
import { useState } from "react";
import { useSession } from "../app/session";
import { api } from "../data";
import { date } from "../lib/format";
import { describeError } from "./States";
import { toast } from "./Toast";
import { currentLang, istCreatorKonto } from "../lib/sprache";

export type ZweiFaktorFuer = "team" | "creator";

/**
 * Erklärtext über der Verwaltung (CREATOR-R3-4): Die Pflicht der Agentur (org_settings.require_mfa) gilt nach 0061 nur
 * fürs Team (app.strong_auth_ok prüft app.is_staff). Creator schützt der Faktor erst, wenn sie ihn selbst einrichten; der
 * Text verspricht ihnen deshalb keine Pflicht der Agentur.
 */
export function zweiFaktorErklaerung(fuer: ZweiFaktorFuer, lang: "de" | "en"): string {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  return fuer === "creator"
    ? t("Nach dem Passwort fragt CreatorOffice zusätzlich einen sechsstelligen Code aus einer Authenticator-App ab. Änderungen an deiner Bankverbindung für Auszahlungen verlangen ihn, sobald du ihn eingerichtet hast. Ohne zweiten Faktor genügt dafür dein Passwort; richte ihn deshalb am besten ein.",
      "After your password, CreatorOffice also asks for a six-digit code from an authenticator app. Changes to your bank details for payouts require it once you have set it up. Without a second factor your password alone is enough, so it is best to set one up.")
    // Studio (Creator-Konto): keine Auszahlungen und keine Vorgabe einer Agentur
    : istCreatorKonto()
    ? t("Nach dem Passwort fragt CreatorOffice zusätzlich einen sechsstelligen Code aus einer Authenticator-App ab. Bankverbindungen und Zahlungseingänge verlangen ihn, sobald er eingerichtet ist.",
      "After your password, CreatorOffice also asks for a six-digit code from an authenticator app. Bank details and payment records require it once it is set up.")
    : t("Nach dem Passwort fragt CreatorOffice zusätzlich einen sechsstelligen Code aus einer Authenticator-App ab. Auszahlungen, Bankverbindungen und Zahlungseingänge verlangen ihn, sobald er eingerichtet ist oder deine Agentur ihn vorschreibt.",
      "After your password, CreatorOffice also asks for a six-digit code from an authenticator app. Payouts, bank details and payment records require it once it is set up or your agency requires it.");
}

/**
 * Zweiter Faktor per Authenticator-App (TOTP) verwalten: einrichten mit QR-Code oder Schlüssel, bestätigen, entfernen.
 * Für Team (Konto · Sicherheit) und Creator (Konto und Sicherheit), deutsch oder englisch. Im Demo-Modus nur ein Hinweis.
 * Wer ihn eingerichtet hat, gibt nach dem Passwort den Code ein; Geldaktionen verlangen ihn (Migration 0061).
 */
export function ZweiFaktorVerwalten({ lang = currentLang(), headingId = "zwei-faktor", fuer = "team" }: { lang?: "de" | "en"; headingId?: string; fuer?: ZweiFaktorFuer }) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const { session } = useSession();
  const qc = useQueryClient();
  const mfa = api.mfa;
  const [neu, setNeu] = useState<{ factorId: string; qrSvg: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ["mfa-state", session?.userId], enabled: !!session && !!mfa, queryFn: () => mfa!.state() });
  const reload = () => qc.invalidateQueries({ queryKey: ["mfa-state"] });
  async function act(fn: () => Promise<void>, done?: string) {
    setBusy(true);
    try {
      await fn();
      if (done) toast(done);
    } catch (e) {
      // Praxistest K-221: Meldungen von Supabase (Code falsch, abgelaufen, Limit) übersetzt statt roh
      if (authFehlerArt(e) !== "unknown") { toast(authFehler(e, lang === "en" ? "en" : "de"), "alert"); return; }
      const d = describeError(e);
      toast(lang === "en" ? "That did not work. Please check the code and try again." : `${d.title} ${d.text}`.trim(), "alert");
    } finally {
      setBusy(false);
    }
  }
  const verified = (q.data?.factors ?? []).filter((f) => f.verified);
  return (
    <section className="panel stack" aria-labelledby={headingId}>
      <h2 id={headingId}>{t("Zweiter Faktor", "Two-step sign-in")}</h2>
      <p>{zweiFaktorErklaerung(fuer, lang)}</p>
      {!mfa ? (
        <div className="notice">{t("Im Demo-Modus gibt es keinen zweiten Faktor. In der echten Anwendung richtest du ihn hier ein.", "The demo has no second factor. In the real application you set it up here.")}</div>
      ) : !q.data ? (
        <p className="muted">{t("Wird geladen …", "Loading …")}</p>
      ) : (
        <>
          {verified.map((f) => (
            <div key={f.id} className="row between">
              <span>{f.name ?? t("Authenticator-App", "Authenticator app")} <small>{t(`eingerichtet am ${date(f.createdAt)}`, `set up on ${date(f.createdAt, undefined, "en-GB")}`)}</small></span>
              <button className="danger" disabled={busy || q.data!.current !== "aal2"}
                onClick={() => void act(async () => { await mfa.unenroll(f.id); await reload(); }, t("Zweiter Faktor entfernt.", "Second factor removed."))}>{t("Entfernen", "Remove")}</button>
            </div>
          ))}
          {verified.length > 0 && q.data.current !== "aal2" && <small>{t("Zum Entfernen bitte einmal mit dem Code anmelden.", "To remove it, sign in once with the code.")}</small>}
          {!verified.length && !neu && (
            <button className="primary" disabled={busy} onClick={() => void act(async () => setNeu(await mfa.enroll(t("Authenticator-App", "Authenticator app"))))}>
              {t("Zweiten Faktor einrichten", "Set up second factor")}
            </button>
          )}
          {neu && (
            <div className="stack">
              <p>{t("1. Scanne den QR-Code mit deiner Authenticator-App oder tippe den Schlüssel ab.", "1. Scan the QR code with your authenticator app or type in the key.")}</p>
              {/* 0181: Schlüssel und QR-Code erscheinen nie im Bild einer Fehlermeldung */}
              <img src={neu.qrSvg} alt={t("QR-Code zum Einrichten", "QR code for setup")} width={180} height={180} className="tk-qr" data-melden-aus="1" />
              <code className="num" data-melden-aus="1" data-sensibel="">{neu.secret}</code>
              <label>
                {t("2. Code aus der App", "2. Code from the app")}
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} />
              </label>
              <div className="row">
                <button className="primary" disabled={busy || code.replace(/\s+/g, "").length !== 6}
                  onClick={() => void act(async () => { await mfa.verify(neu.factorId, code); setNeu(null); setCode(""); await reload(); }, t("Zweiter Faktor ist eingerichtet.", "Second factor is set up."))}>
                  {t("Bestätigen", "Confirm")}
                </button>
                <button onClick={() => { setNeu(null); setCode(""); }}>{t("Abbrechen", "Cancel")}</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
